import React, { useRef, useState } from "react";
import { Alert, Button, Stack, TextField, Typography } from "@mui/material";
import { adminFetch } from "../services/adminFetch";

const endpoint = "/api/admin/draws/104/winner-configuration";
const operationStorageKey = "fliplottery.draw104.winner-configuration.operation";
const uuidV4 = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function prepareOperationId() {
  const stored = localStorage.getItem(operationStorageKey);
  const operationId = uuidV4.test(stored || "") ? stored.toLowerCase() : crypto.randomUUID();
  // Persist before sending so a reload can safely retry an uncertain request.
  localStorage.setItem(operationStorageKey, operationId);
  return operationId;
}

function isVerifiedResponse(response, operationId) {
  const configuration = response?.configuration;
  const auditId = configuration?.audit_id;
  const validAuditId = (Number.isSafeInteger(auditId) && auditId > 0)
    || (typeof auditId === "string" && /^[1-9][0-9]*$/.test(auditId));

  return response?.status === true
    && configuration?.draw_id === 104
    && configuration.configured_winner_count === 60
    && configuration.status === "completed"
    && configuration.operation_id === operationId
    && typeof configuration.changed === "boolean"
    && (validAuditId || (configuration.changed === false && auditId === null));
}

export default function Draw104WinnerConfiguration({ draw, onVerified, onClose }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [attempted, setAttempted] = useState(false);
  const [verified, setVerified] = useState(null);
  const lock = useRef(false);
  const operationId = useRef(null);

  async function save() {
    if (lock.current || verified || draw?.status !== "completed") return;
    lock.current = true;
    setBusy(true);
    setError("");

    try {
      if (!operationId.current) operationId.current = prepareOperationId();
    } catch (_error) {
      setError("Unable to prepare a safe retry in this browser. Enable site storage and try again.");
      lock.current = false;
      setBusy(false);
      return;
    }

    setAttempted(true);
    try {
      const response = await adminFetch(endpoint, {
        method: "PUT",
        body: JSON.stringify({ configured_winner_count: 60, operation_id: operationId.current })
      });

      if (!isVerifiedResponse(response, operationId.current)) {
        const detail = response?.status === false && response.message ? `${response.message} ` : "";
        setError(`${detail}The configuration is not confirmed. Retry and verify using the same request.`);
        return;
      }

      setVerified(response.configuration);
      // A verified request is finished. Only uncertain requests need to survive reopening.
      try {
        if (localStorage.getItem(operationStorageKey) === operationId.current) {
          localStorage.removeItem(operationStorageKey);
        }
      } catch (_error) {
        // The verified receipt remains valid even if browser storage becomes unavailable.
      }
      onVerified(response.configuration);
    } catch (_error) {
      setError("The configuration is not confirmed. Retry and verify using the same request.");
    } finally {
      lock.current = false;
      setBusy(false);
    }
  }

  return (
    <Stack spacing={2.5}>
      <Typography variant="h6" fontWeight={700}>Draw 104 winner configuration</Typography>
      <Alert severity="warning">
        Draw 104 remains Completed. Saving only updates its configured winner count to 60.
        It does not rerun the draw, add winners, restore tickets or change payouts.
      </Alert>
      <Typography>Current configured winners: {draw?.configured_winner_count ?? "Unavailable"}</Typography>
      <TextField label="Configured winner count" type="number" value={60} InputProps={{ readOnly: true }}
        helperText="Only 60 is permitted for this configuration update." />
      {draw?.status !== "completed" && <Alert severity="error">Draw 104 must be Completed before its configuration can be updated.</Alert>}
      {error && <Alert severity="error">{error}</Alert>}
      {verified && <Alert severity="success">
        {verified.changed ? "Configured winner count saved as 60." : "Configured winner count verified as 60. No change was needed."}
        {" Draw 104 remains Completed."}
        {verified.audit_id !== null && ` Audit record: ${verified.audit_id}.`}
      </Alert>}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
        <Button variant="contained" onClick={save} disabled={busy || Boolean(verified) || draw?.status !== "completed"}>
          {busy ? "Verifying configuration…" : attempted && !verified ? "Retry and verify configuration" : "Save winner configuration"}
        </Button>
        <Button variant="outlined" onClick={onClose} disabled={busy}>{verified ? "Close configuration" : "Cancel Edit"}</Button>
      </Stack>
    </Stack>
  );
}
